import { Controller } from "@hotwired/stimulus"
import * as pdfjsLib from "pdfjs-dist"

pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs"

// PDF Previewer - 스크롤 방식 (모든 페이지 렌더링)
export default class extends Controller {
  static values = {
    url: String
  }

  static targets = ["pagesContainer", "pageInfo", "emptyState"]

  connect() {
    this.pdfDoc = null
    this.rendering = false
    this.totalPages = 0
    // 여기서 loadPdf 를 부르지 않는다. Stimulus 가 컨트롤러를 붙일 때
    // urlValueChanged 를 이미 한 번 호출하므로, 여기서 또 부르면 **같은 PDF 를 두 번
    // 내려받는다.**
    //
    // 예전에는 "urlValueChanged 가 init 시 누락될 수 있어" 라는 이유로 직접 한 번 더
    // 불렀다. 그런데 운영 로그에는 매 클릭마다 요청이 정확히 두 번 찍혔고(15ms 간격,
    // 서로 다른 요청 ID), **두 번째가 자주 중단됐다**(Completed 줄 없음).
    // loadPdf 가 시작할 때 기존 pdfDoc 을 destroy() 하므로, 뒤엣것이 앞엣것이 받는 중인
    // 문서를 끊는다 — 타이밍에 따라 미리보기가 빈 채로 남는다. "되던 게 가끔 안 되는"
    // 증상의 정체였다(2026-08-25).
  }

  disconnect() {
    if (this.pdfDoc) {
      this.pdfDoc.destroy()
      this.pdfDoc = null
    }
  }

  urlValueChanged() {
    if (this.urlValue) {
      this.loadPdf(this.urlValue)
    } else {
      this.showEmptyState()
    }
  }

  async loadPdf(url) {
    this.hideEmptyState()

    if (this.pdfDoc) {
      this.pdfDoc.destroy()
      this.pdfDoc = null
    }

    try {
      // verbosity 0 = 에러만(기본값은 경고까지). MSDS 원본은 스캔·변환된 문서가 많아
      // 임베드 폰트가 깨져 있는 일이 흔한데, 그때마다 pdf.js 가
      // "Failed to load font 'g_d3_f1'" 같은 경고를 쏟는다. 우리가 고칠 수 있는 것이
      // 아니고(원본 PDF 의 문제다) pdf.js 는 대체 폰트로 계속 그리므로 화면은 멀쩡하다.
      // 남겨 두면 진짜 원인을 찾을 때 콘솔이 그걸로 덮인다.
      this.pdfDoc = await pdfjsLib.getDocument({ url, verbosity: 0 }).promise
      this.totalPages = this.pdfDoc.numPages
      this.renderAllPages()
      this._togglePdfLoaded(true)
    } catch (error) {
      console.error("PDF 로드 실패:", error)
      this.showEmptyState()
    }
  }

  async renderAllPages() {
    if (!this.pdfDoc || this.rendering) return
    this.rendering = true

    const container = this.pagesContainerTarget
    container.innerHTML = ""

    try {
      for (let num = 1; num <= this.totalPages; num++) {
        const page = await this.pdfDoc.getPage(num)

        const canvas = document.createElement("canvas")
        canvas.className = "w-full rounded-md border border-[var(--gray-200)]"

        // 컨테이너 너비에 맞게 scale 계산 (고해상도 대응)
        const containerWidth = container.clientWidth
        const originalViewport = page.getViewport({ scale: 1 })
        const scale = containerWidth / originalViewport.width
        const dpr = window.devicePixelRatio || 1
        const viewport = page.getViewport({ scale: scale * dpr })

        canvas.width = viewport.width
        canvas.height = viewport.height
        canvas.style.width = containerWidth + "px"
        canvas.style.height = (viewport.height / dpr) + "px"

        container.appendChild(canvas)

        const ctx = canvas.getContext("2d")
        await page.render({ canvasContext: ctx, viewport }).promise

        // 페이지 번호 표시
        const pageLabel = document.createElement("div")
        pageLabel.className = "text-xs text-[var(--gray-400)] text-center py-1"
        pageLabel.textContent = `${num} / ${this.totalPages}`
        container.appendChild(pageLabel)
      }

      this.updatePageInfo()
    } catch (error) {
      // 렌더 취소는 **실패가 아니다.** 앞 문서를 그리는 중에 뷰어가 비워지거나 다른
      // 문서로 바뀌면 pdf.js 가 남은 페이지 렌더를 취소하며 이걸 던진다 — 정상 동작이다.
      // console.error 로 찍으면 진짜 원인을 찾을 때 소음이 된다(실제로 MSDS 후보 선택
      // 회귀를 쫓는 중에 이게 섞여 나왔다).
      if (error && error.name === "RenderingCancelledException") return

      console.error("페이지 렌더링 실패:", error)
    } finally {
      this.rendering = false
    }
  }

  updatePageInfo() {
    if (this.hasPageInfoTarget) {
      this.pageInfoTarget.textContent = `전체 ${this.totalPages}페이지`
    }
  }

  showEmptyState() {
    if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.remove("hidden")
    if (this.hasPagesContainerTarget) this.pagesContainerTarget.parentElement.classList.add("hidden")
    if (this.hasPageInfoTarget) this.pageInfoTarget.classList.add("hidden")
    this._togglePdfLoaded(false)
  }

  // 부모 wrapper (.msds-preview-wrapper) 에 pdf-loaded 클래스 toggle.
  // 검색 탭이 이걸로 A4 aspect-ratio 확장 (CSS 는 application.css 에 정의).
  _togglePdfLoaded(loaded) {
    const wrapper = this.element.closest(".msds-preview-wrapper")
    if (wrapper) wrapper.classList.toggle("pdf-loaded", loaded)
  }

  hideEmptyState() {
    if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.add("hidden")
    if (this.hasPagesContainerTarget) this.pagesContainerTarget.parentElement.classList.remove("hidden")
    if (this.hasPageInfoTarget) this.pageInfoTarget.classList.remove("hidden")
  }
}
